import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ChartConfig }                         from '../chart';
import { ChartHelper }                         from '../chart-helper';

/**
 * Radial gauge (Power BI style): a 180° arc filled to the current value's position
 * between min and max, the value writ large under the arc's center, min/max at the
 * ends, and an optional target shown as a tick across the band with its own label.
 * Data: datasets[0].data[0] is the value (its dataset label is the unit caption);
 * min/max/target ride on `data` (defaults: min 0, max 2× value — Power BI's default).
 */
@Component({
	standalone:  false,
	selector:    '[cs-gauge-chart]',
	templateUrl: './gauge-chart.component.html'
})
export class CsGaugeChartComponent implements OnInit, OnChanges {

	@Input() config: ChartConfig;

	trackPath = '';
	fillPath  = '';
	targetTick: { x1: number; y1: number; x2: number; y2: number; lx: number; ly: number; anchor: string } | null = null;

	valueText  = '';
	unitText   = '';
	targetText = '';
	minText    = '';
	maxText    = '';

	cx = 0;
	cy = 0;
	rOuter = 0;
	rInner = 0;

	private isDataReady() {
		return this.config?.data?.datasets?.length && this.config.data.datasets[0].data?.length;
	}

	ngOnInit() {
		setTimeout(() => {
			if (this.isDataReady()) { this.calculate(); }
		}, 1);
	}

	ngOnChanges(changes: any) {
		if (changes.hasOwnProperty('config') && this.isDataReady()) {
			this.calculate();
		}
	}

	private calculate() {
		const w = this.config.chartWidth  - this.config.xPadding * 2;
		const h = this.config.chartHeight - this.config.yPadding * 2;
		if (!w || !h || w < 20 || h < 20) { return; }

		const value  = this.config.data.datasets[0].data[0] ?? 0;
		const min    = this.config.data.min ?? 0;
		const max    = this.config.data.max ?? (value > min ? value * 2 : min + 1);
		const target = this.config.data.target;

		// The semicircle + the value line below it must fit the box; leave a label band.
		const bandWidth = 34;
		this.rOuter = Math.min((w - 90) / 2, h - 56);
		this.rInner = this.rOuter - bandWidth;
		this.cx = this.config.xPadding + w / 2;
		this.cy = this.config.yPadding + 8 + this.rOuter;

		const span  = Math.max(max - min, 1e-9);
		const clamp = (v: number) => Math.min(1, Math.max(0, (v - min) / span));

		this.trackPath = this.bandPath(0, 1);
		this.fillPath  = this.bandPath(0, clamp(value));

		if (target != null && Number.isFinite(target)) {
			const a  = this.angleAt(clamp(target));
			const p1 = this.polar(this.rInner - 4, a);
			const p2 = this.polar(this.rOuter + 4, a);
			const pl = this.polar(this.rOuter + 16, a);
			const onLeftHalf = clamp(target) < 0.5;
			this.targetTick = {
				x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
				lx: pl.x, ly: pl.y,
				anchor: onLeftHalf ? 'end' : 'start'
			};
			this.targetText = ChartHelper.getValueFormatted(Math.round(target));
		} else {
			this.targetTick = null;
			this.targetText = '';
		}

		this.valueText = ChartHelper.getValueLocale(value);
		this.unitText  = this.config.data.datasets[0].label || '';
		this.minText   = ChartHelper.getValueFormatted(Math.round(min));
		this.maxText   = ChartHelper.getValueFormatted(Math.round(max));
	}

	/** Annular band sector between two fractions of the 180° sweep (0 = left end, 1 = right). */
	private bandPath(from: number, to: number): string {
		if (to <= from) { return ''; }
		const a0 = this.angleAt(from), a1 = this.angleAt(to);
		const o0 = this.polar(this.rOuter, a0), o1 = this.polar(this.rOuter, a1);
		const i0 = this.polar(this.rInner, a0), i1 = this.polar(this.rInner, a1);
		// The whole gauge sweeps 180°, so a band sector NEVER exceeds it — the SVG
		// large-arc flag stays 0 (setting it routes the arc the long way around and
		// renders a self-intersecting crescent).
		return `M ${o0.x} ${o0.y} A ${this.rOuter} ${this.rOuter} 0 0 1 ${o1.x} ${o1.y} `
			+ `L ${i1.x} ${i1.y} A ${this.rInner} ${this.rInner} 0 0 0 ${i0.x} ${i0.y} Z`;
	}

	/** Fraction of the sweep → angle in radians (π at the left end, 0 at the right). */
	private angleAt(fraction: number): number {
		return Math.PI * (1 - fraction);
	}

	private polar(r: number, angle: number): { x: number; y: number } {
		return {
			x: Math.round((this.cx + r * Math.cos(angle)) * 10) / 10,
			y: Math.round((this.cy - r * Math.sin(angle)) * 10) / 10
		};
	}
}
